import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { EventsProvider } from "@/contexts/eventscontent";
import ScrollToTop from "@/components/ui/scroll-to-top";
import ScrollToTopOnRouteChange from "@/components/ui/scroll-to-top-on-route";
import Loader from "@/components/ui/loader";
import Layout from "./components/layout/Layout";
import { Accessibility } from "accessibility";
import { injectChurchSchema } from "@/utils/schemaOrg";
import SmoothScroll from "@/components/SmoothScroll";

// ✅ Lazy-loaded pages
const Home = lazy(() => import("./pages/Home"));
const About = lazy(() => import("./pages/About"));
const Services = lazy(() => import("./pages/Services"));
const Events = lazy(() => import("./pages/Events"));
const EventDetailsPage = lazy(() => import("./pages/EventDetails"));
const SocialDisclaimer = lazy(() => import("./pages/legal/SocialDisclaimer"));
const Terms = lazy(() => import("./pages/legal/Terms"));
const Sermons = lazy(() => import("./pages/Sermons"));
const VideoWatch = lazy(() => import("./pages/VideoWatch"));
const Sundays = lazy(() => import("./pages/Sundays"));
const SoonStore = lazy(() => import("./pages/SoonStore"));
const Join = lazy(() => import("./pages/Join"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LiveStream = lazy(() => import("./pages/LiveStream"));
const GDPR = lazy(() => import("./pages/legal/Gdpr"));
const Privacy = lazy(() => import("./pages/legal/Privacy"));
const MediaTeam = lazy(() => import("./pages/ministries/MediaTeam"));
const MusicTeam = lazy(() => import("./pages/ministries/MusicTeam"));
const HospitalityTeam = lazy(
  () => import("./pages/ministries/HospitalityTeam")
);
const CreativeArtsTeam = lazy(
  () => import("./pages/ministries/CreativeArtsTeam")
);
const MensMinistry = lazy(() => import("./pages/ministries/MensMinistry"));
const YouthMinistry = lazy(() => import("./pages/ministries/YouthMinistry"));
const WomensMinistry = lazy(() => import("./pages/ministries/WomensMinistry"));
const ChildrensMinistry = lazy(
  () => import("./pages/ministries/ChildrensMinistry")
);
const EvangelismMinistry = lazy(
  () => import("./pages/ministries/EvangelismMinistry")
);
const District = lazy(() => import("./pages/district"));
const Podcast = lazy(() => import("./pages/TOTC"));
const Discipleship = lazy(() => import("./pages/discipleship"));
const Donate = lazy(() => import("./pages/giving/Donate"));
const FoodBank = lazy(() => import("./pages/giving/FoodBank"));
const GiftAid = lazy(() => import("./pages/giving/GiftAid"));
const DonationSuccess = lazy(() => import("./pages/giving/DonationSuccess"));
const Safeguarding = lazy(() => import("./pages/safeguarding/Safeguarding"));
const ReportingAbuse = lazy(() => import("./pages/safeguarding/ReportingAbuse"));
const Students = lazy(() => import("./pages/Students"));


const queryClient = new QueryClient();

const App = () => {
  useEffect(() => {
    // Inject Schema.org structured data for church
    injectChurchSchema();
    
    // ✅ Initialise accessibility widget with custom dyslexia-friendly font function
    new Accessibility({
      textToSpeechLang: "en-GB",
      labels: {
        resetTitle: "Reset accessibility settings",
        closeTitle: "Close accessibility toolbar",
        readableFont: "Readable Font",
      },
      icon: {
        circular: true,
        position: {
          bottom: { size: 50, units: "%" },
          left: { size: 2, units: "rem" },
          type: "fixed",
        },
      },
      fontFamily: "'Inter', sans-serif",
      invertImages: false,

      // ✅ Custom function for dyslexia-friendly font
      customFunctions: [
        {
          method: (cf, state) => {
            if (state) {
              // Apply dyslexia-friendly font (OpenDyslexic)
              const link = document.createElement("link");
              link.id = "dyslexia-font-link";
              link.rel = "stylesheet";
              link.href = "https://fonts.cdnfonts.com/css/opendyslexic";
              document.head.appendChild(link);

              // Create or update style element for global font override
              let styleEl = document.getElementById("dyslexia-font-style");
              if (!styleEl) {
                styleEl = document.createElement("style");
                styleEl.id = "dyslexia-font-style";
                document.head.appendChild(styleEl);
              }

              styleEl.textContent = `
              * {
                font-family: 'OpenDyslexic', 'Comic Sans MS', sans-serif !important;
              }
              body, div, p, span, h1, h2, h3, h4, h5, h6, a, button, input, textarea, select, label, li, td, th {
                letter-spacing: 0.12em !important;
                line-height: 1.8 !important;
                word-spacing: 0.16em !important;
              }
            `;
            } else {
              // Revert to normal font
              const link = document.getElementById("dyslexia-font-link");
              if (link) {
                link.remove();
              }

              const styleEl = document.getElementById("dyslexia-font-style");
              if (styleEl) {
                styleEl.remove();
              }
            }
          },
          buttonText: "Dyslexia Font",
          id: 1,
          toggle: true,
          icon: "text_fields",
          emoji: "📖",
        },
      ],
    });
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTopOnRouteChange />
          <SmoothScroll />
          <EventsProvider>
            <Layout>
              <Suspense fallback={<Loader />}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/sundays" element={<Sundays />} />
                  <Route path="/events" element={<Events />} />
                  <Route path="/events/:id" element={<EventDetailsPage />} />
                  <Route path="/totc" element={<Podcast />} />
                  <Route path="/discipleship" element={<Discipleship />} />
                  <Route path="/sermons" element={<Sermons />} />
                  <Route path="/watch/:category/:videoId" element={<VideoWatch />} />
                  <Route path="/store" element={<SoonStore />} />
                  <Route path="/join" element={<Join />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/live" element={<LiveStream />} />
                  <Route path="/safeguarding" element={<Safeguarding />} />
                  <Route path="/safeguarding/reporting-abuse" element={<ReportingAbuse />} />
                  <Route path="/gdpr" element={<GDPR />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route
                    path="/social-disclaimer"
                    element={<SocialDisclaimer />}
                  />
                  <Route path="/terms" element={<Terms />} />
                  <Route
                    path="/ministries/media"
                    element={<MediaTeam />}
                  />
                  <Route
                    path="/ministries/music"
                    element={<MusicTeam />}
                  />
                  <Route
                    path="/ministries/hospitality"
                    element={<HospitalityTeam />}
                  />
                  <Route
                    path="/ministries/creativearts"
                    element={<CreativeArtsTeam />}
                  />
                  <Route
                    path="/ministries/men"
                    element={<MensMinistry />}
                  />
                  <Route
                    path="/ministries/youth"
                    element={<YouthMinistry />}
                  />
                  <Route
                    path="/ministries/women"
                    element={<WomensMinistry />}
                  />
                  <Route
                    path="/ministries/kids"
                    element={<ChildrensMinistry />}
                  />
                  <Route
                    path="/ministries/evangelism"
                    element={<EvangelismMinistry />}
                  />
                  <Route
                    path="/students"
                    element={<Students />}
                  />
                  <Route path="/district" element={<District />} />
                  <Route path="/donations" element={<Donate />} />
                  <Route path="/foodbank" element={<FoodBank />} />
                  <Route path="/gift-aid" element={<GiftAid />} />
                  <Route path="/donation-success" element={<DonationSuccess />} />


                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </Layout>
          </EventsProvider>
          <ScrollToTop />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
